import { queryOptions, useQuery } from '@tanstack/react-query';
import { type CoinFee, filterCoinFeesByCategory, getCoinFees, getCustomTradingFees, getFeeExclusions, unwrapCustomTradingFees, unwrapFeeExclusions } from '@/services/api/fees';

// Stable select references — an inline closure recreated per render defeats
// TanStack Query's select memoization (fiat/crypto share one cached fetch).
const selectFiatFees = (fees: CoinFee[]): CoinFee[] => filterCoinFeesByCategory(fees, 'fiat');
const selectCryptoFees = (fees: CoinFee[]): CoinFee[] => filterCoinFeesByCategory(fees, 'crypto');

export const FEES_QUERY_KEY = ['operations', 'fees'] as const;
export const COIN_FEES_QUERY_KEY = [...FEES_QUERY_KEY, 'coins'] as const;
export const FEE_EXCLUSIONS_QUERY_KEY = [...FEES_QUERY_KEY, 'exclusions'] as const;
export const CUSTOM_TRADING_FEES_QUERY_KEY = [...FEES_QUERY_KEY, 'trading'] as const;

// Loading semantics: fiat/crypto and exclusions fetch once and only
// refetch on explicit invalidation (legacy "fetch only if empty") — hence the
// infinite staleTime. Coin-fee updates invalidate COIN_FEES_QUERY_KEY.
export const fiatFeesQueryOptions = () =>
  queryOptions({
    queryKey: COIN_FEES_QUERY_KEY,
    queryFn: getCoinFees,
    select: selectFiatFees,
    staleTime: Number.POSITIVE_INFINITY,
  });

export const cryptoFeesQueryOptions = () =>
  queryOptions({
    queryKey: COIN_FEES_QUERY_KEY,
    queryFn: getCoinFees,
    select: selectCryptoFees,
    staleTime: Number.POSITIVE_INFINITY,
  });

export const useFiatFees = () => useQuery(fiatFeesQueryOptions());
export const useCryptoFees = () => useQuery(cryptoFeesQueryOptions());

export const feeExclusionsQueryOptions = () =>
  queryOptions({
    queryKey: FEE_EXCLUSIONS_QUERY_KEY,
    queryFn: getFeeExclusions,
    select: unwrapFeeExclusions,
    staleTime: Number.POSITIVE_INFINITY,
  });

export const useFeeExclusions = () => useQuery(feeExclusionsQueryOptions());

// KEEP: trading fees may be edited elsewhere — default staleTime 0;
// the page additionally refetches explicitly on every tab activation.
export const customTradingFeesQueryOptions = () =>
  queryOptions({
    queryKey: CUSTOM_TRADING_FEES_QUERY_KEY,
    queryFn: getCustomTradingFees,
    select: unwrapCustomTradingFees,
  });

export const useCustomTradingFees = () => useQuery(customTradingFeesQueryOptions());
